import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import api from '../../../api/api';

const columns = [['name', 'Актив'], ['inventory_number', 'Инв. номер'], ['point', 'ПВЗ / откуда'], ['address', 'Адрес'], ['status', 'Статус']];

export default function CourierPage() {
  const [items, setItems] = useState([]), [points, setPoints] = useState([]);
  const [search, setSearch] = useState(''), [query, setQuery] = useState('');
  const [point, setPoint] = useState(''), [state, setState] = useState('all');
  const [sort, setSort] = useState('name'), [direction, setDirection] = useState('asc');
  const [page, setPage] = useState(0), [limit, setLimit] = useState(25), [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(null);
  const [error, setError] = useState(''), [notice, setNotice] = useState(''), [pointsError, setPointsError] = useState('');
  const version = useRef(0), submitting = useRef(false), requests = useRef(new Map());
  const cancelFetch = useCallback(() => { ++version.current; }, []);
  const fetchAssets = useCallback(async () => {
    const current = ++version.current; setLoading(true); setError('');
    try {
      const { data } = await api.get('/locker/courier/assets', { params: { search: query, point_id: point || undefined,
        state, sort_by: sort, sort_order: direction, page: page + 1, limit } });
      if (current !== version.current) return;
      if (page > 0 && page * limit >= data.total) { setPage(Math.max(0, Math.ceil(data.total / limit) - 1)); return; }
      setItems(data.items); setTotal(data.total);
    } catch (err) {
      if (current === version.current) { setItems([]); setTotal(0); setError('Не удалось загрузить активы. Нажмите «Обновить».'); }
    } finally { if (current === version.current) setLoading(false); }
  }, [query, point, state, sort, direction, page, limit]);
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    api.get('/rental-points/').then(({ data }) => { if (active) setPoints(data); })
      .catch(() => { if (active) setPointsError('Не удалось загрузить фильтр ПВЗ. Поиск по названию и адресу доступен.'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    fetchAssets();
    const refresh = () => { if (!submitting.current) fetchAssets(); };
    window.addEventListener('focus', refresh);
    return () => { cancelFetch(); window.removeEventListener('focus', refresh); };
  }, [fetchAssets, cancelFetch]);
  const handover = async asset => {
    if (submitting.current) return;
    submitting.current = true; setBusy(asset.id); setError(''); setNotice('');
    const key = `${asset.id}:${asset.point_id}`;
    const requestId = requests.current.get(key) || crypto.randomUUID();
    requests.current.set(key, requestId);
    try {
      await api.post(`/locker/courier/assets/${asset.id}/handover`, { source_point_id: asset.point_id, request_id: requestId });
      requests.current.delete(key);
      setNotice(`${asset.name} передан курьеру. Теперь его можно разместить через программу постамата.`);
      await fetchAssets();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось подтвердить передачу. Обновите список или повторите запрос.');
    } finally { submitting.current = false; setBusy(null); }
  };
  return <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
      <Typography variant="h4" component="h1">Курьер</Typography>
      <Button onClick={fetchAssets} disabled={loading || busy !== null}>Обновить</Button>
    </Box>
    <Typography color="text.secondary" mb={2}>Курьер получает вещи по кодам в своём приложении. Здесь можно отметить передачу вручную и следить за размещением.</Typography>
    <Button href="/courier-app/" target="_blank" rel="noopener" variant="outlined" sx={{ mb: 3 }}>Открыть приложение курьера</Button>
    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
      <TextField label="Название, код или адрес" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: '1 1 300px' }} disabled={busy !== null} />
      <TextField select label="ПВЗ / откуда" value={point} onChange={e => { setPoint(e.target.value); setPage(0); }} sx={{ minWidth: 210 }} disabled={busy !== null}>
        <MenuItem value="">Все пункты</MenuItem>
        {points.map(p => <MenuItem key={p.id} value={p.id}>{p.name}{p.address ? ` · ${p.address}` : ''}</MenuItem>)}
      </TextField>
      <TextField select label="Состояние" value={state} onChange={e => { setState(e.target.value); setPage(0); }} sx={{ minWidth: 210 }} disabled={busy !== null}>
        <MenuItem value="all">Все активы</MenuItem><MenuItem value="available">Можно передать</MenuItem>
        <MenuItem value="in_transit">У курьера</MenuItem><MenuItem value="in_cell">В ячейке</MenuItem>
      </TextField>
    </Box>
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {pointsError && <Alert severity="warning" sx={{ mb: 2 }}>{pointsError}</Alert>}
    <TableContainer component={Paper}>
      <Table aria-label="Активы для передачи курьеру">
        <TableHead><TableRow>{columns.map(([key, title]) => <TableCell key={key} sortDirection={sort === key ? direction : false}>
          <TableSortLabel active={sort === key} direction={sort === key ? direction : 'asc'} disabled={busy !== null}
            onClick={() => { setDirection(sort === key && direction === 'asc' ? 'desc' : 'asc'); setSort(key); setPage(0); }}>{title}</TableSortLabel>
        </TableCell>)}<TableCell>Курьер / передано</TableCell><TableCell>Действие</TableCell></TableRow></TableHead>
        <TableBody>{loading ? <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={26} aria-label="Загрузка активов" /></TableCell></TableRow> :
          items.length === 0 ? <TableRow><TableCell colSpan={7}>Активы не найдены</TableCell></TableRow> : items.map(asset => <TableRow key={asset.id}>
            <TableCell><Link component={RouterLink} to={`/assets/edit/${asset.id}`}>{asset.name}</Link><Typography variant="caption" display="block">{asset.scan_code}</Typography></TableCell>
            <TableCell>{asset.inventory_number || '—'}</TableCell>
            <TableCell>{asset.in_transit ? 'Из: ' : ''}{asset.point_name || 'Не указан'}</TableCell>
            <TableCell>{asset.address || '—'}</TableCell>
            <TableCell><Chip size="small" label={asset.status_label} color={asset.in_transit ? 'warning' : 'default'} />{asset.locker_cell_id && <Typography variant="caption" display="block">В ячейке</Typography>}</TableCell>
            <TableCell>{asset.in_transit && <Typography variant="body2">{asset.courier_name || 'Получение в приложении не отмечено'}</Typography>}{asset.handed_over_at ? new Date(asset.handed_over_at).toLocaleString('ru-RU') : '—'}</TableCell>
            <TableCell>{asset.can_handover ? <Button variant="contained" onClick={() => handover(asset)} disabled={busy !== null} sx={{ whiteSpace: 'nowrap' }}>
              {busy === asset.id ? 'Передаём…' : 'Передать курьеру'}</Button> : <Typography variant="body2" color="text.secondary">{asset.in_transit ? 'Ожидает размещения' : 'Передача недоступна'}</Typography>}</TableCell>
          </TableRow>)}</TableBody>
      </Table>
      <TablePagination component="div" count={total} page={page} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="На странице" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        onPageChange={(_, next) => setPage(next)} onRowsPerPageChange={e => { setLimit(Number(e.target.value)); setPage(0); }} />
    </TableContainer>
  </Box>;
}
